// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import type { ChangeEvent, DragEvent, FC, RefObject } from 'react';
import { DocumentFileListItem } from '../document-file-list-item/document-file-list-item';
import type { UploadFileValidationError } from '../../utils/validate-upload-files';

const FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.docx,.csv';

export interface UploadDocumentFilesSectionProps {
  readonly fileInputRef: RefObject<HTMLInputElement>;
  readonly selectedFiles: readonly File[];
  readonly isDragging: boolean;
  readonly fileError: UploadFileValidationError | null;
  readonly fileErrorMessage: string | null;
  readonly filesSummaryLabel: string;
  readonly onFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onSelectFileClick: () => void;
  readonly onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  readonly onDragLeave: () => void;
  readonly onDrop: (event: DragEvent<HTMLDivElement>) => void;
  readonly onRemoveFile: (index: number) => void;
}

export const UploadDocumentFilesSection: FC<UploadDocumentFilesSectionProps> = ({
  fileInputRef,
  selectedFiles,
  isDragging,
  fileErrorMessage,
  filesSummaryLabel,
  onFileInputChange,
  onSelectFileClick,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveFile,
}) => (
  <div className="flex flex-col gap-3">
    <Typography
      variant="body2"
      className="!text-sm !font-semibold !uppercase !tracking-wide !text-primary-medium"
    >
      <Translation i18nKey="documents.upload-drawer.files-title" ns="components" />
    </Typography>

    {selectedFiles.length > 0 ? (
      <ul
        className="flex flex-col gap-3 rounded-lg bg-section p-3"
        data-testid="upload-document-files-list"
      >
        {selectedFiles.map((file, index) => (
          <DocumentFileListItem
            key={`${file.name}-${file.size}-${file.lastModified}`}
            fileName={file.name}
            fileSize={file.size}
            testId={`upload-document-file-${index}`}
            action={
              <IconButton
                icon="Close"
                variant="icon"
                size="lg"
                color="main"
                className="!p-1"
                aria-label="Remove file"
                onClick={() => onRemoveFile(index)}
                data-testid={`upload-document-remove-file-${index}`}
              />
            }
          />
        ))}
      </ul>
    ) : null}

    <div
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectFileClick();
        }
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onSelectFileClick}
      className={classNames(
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-6 text-center transition-colors',
        isDragging
          ? 'border-secondary bg-secondary/5'
          : 'border-primary-light bg-page',
      )}
      data-testid="upload-document-drop-zone"
    >
      <AppIcon icon="Upload" size="lg" color="secondary" />
      <Typography variant="body2" className="!font-semibold !text-secondary">
        <Translation i18nKey="documents.upload-drawer.drag-here" ns="components" />
      </Typography>
      <Typography variant="body2" className="!text-primary-medium">
        <Translation
          i18nKey="documents.upload-drawer.file-constraints"
          ns="components"
        />
      </Typography>
      <Button
        type="button"
        variant="outlined"
        color="secondary"
        className="normal-case"
        onClick={event => {
          event.stopPropagation();
          onSelectFileClick();
        }}
        data-testid="upload-document-select-file"
      >
        <Translation
          i18nKey="documents.upload-drawer.select-files"
          ns="components"
        />
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_ACCEPT}
        multiple
        className="sr-only"
        onChange={onFileInputChange}
        data-testid="upload-document-file-input"
      />
    </div>

    {selectedFiles.length > 0 ? (
      <Typography variant="body2" className="!text-primary-medium">
        {filesSummaryLabel}
      </Typography>
    ) : null}

    {fileErrorMessage ? (
      <Typography
        variant="body2"
        className="!text-error-main"
        data-testid="upload-document-file-error"
      >
        {fileErrorMessage}
      </Typography>
    ) : null}
  </div>
);
